import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../utils/api';
import MenuPickerBottomSheet from './menuPickerBottomSheet';

/**
 * 판매시간 일괄 적용 모달.
 * - 메뉴 편집에서 판매시간 수정 + 저장 시 호출됨.
 * - 1단계: "다른 메뉴들도 일괄 적용하시겠습니까?" 확인 팝업
 * - 2단계: MenuPickerBottomSheet (프린터 매핑과 동일 패턴) → 메뉴 선택 시 즉시 적용
 *
 * salesTime = { is_sales_time, start_time, end_time } — 방금 저장한 판매시간 값
 */
const SalesTimeBulkApply = ({ isOpen, onClose, onApplied, storeId, salesTime }) => {
  const [step, setStep] = useState('confirm'); // 'confirm' | 'picker' | 'closing'
  const [selectedKeys, setSelectedKeys] = useState([]); // 선택된 menu_key (적용 버튼 누르기 전까지 DB 미반영)
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimerRef = useRef(null);
  const appliedRef = useRef(false); // 이번 세션에 1개 이상 적용했는지 (닫을 때 부모 반영)

  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: true, message, type });
    toastTimerRef.current = setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 1800);
  };

  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setSelectedKeys([]);
      appliedRef.current = false;
    }
  }, [isOpen]);

  // 메뉴 선택 — 적용 버튼을 누르기 전까지는 선택만 누적 (DB 미반영)
  const handleSelectMenu = (menuKey) => {
    setSelectedKeys(prev => [...prev.filter(k => k !== menuKey), menuKey]);
  };

  // 선택 해제
  const handleDeselectMenu = (menuKey) => {
    setSelectedKeys(prev => prev.filter(k => k !== menuKey));
  };

  // 적용 버튼 — 선택한 메뉴 전체에 판매시간을 한 번의 요청으로 일괄 적용
  const handleConfirm = async () => {
    if (!storeId || selectedKeys.length === 0) return;
    try {
      const r = await apiRequest(`/menus/${storeId}/bulk-sales-time`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          menu_keys: selectedKeys,
          is_sales_time: salesTime?.is_sales_time || false,
          start_time: salesTime?.start_time || null,
          end_time: salesTime?.end_time || null,
        }),
      });
      if (r.success) {
        appliedRef.current = true;
      } else {
        showToast(r.message || '적용 실패', 'error');
        return;
      }
    } catch (e) {
      showToast('적용 요청 실패: ' + e.message, 'error');
      return;
    }
    handlePickerClose();
  };

  // 전체 닫기 — 적용 이력이 있으면 부모에 즉시 반영(메뉴 재조회) 요청
  const closeAll = () => {
    if (appliedRef.current && onApplied) onApplied();
    appliedRef.current = false;
    onClose();
  };

  // picker 뒤로가기/닫기 — 슬라이드다운 애니메이션(약 200ms) 후 전체 닫기
  const handlePickerClose = () => {
    setStep('closing'); // isOpen={step==='picker'} 가 false → picker 가 슬라이드다운
    setTimeout(closeAll, 220);
  };

  if (!isOpen) return null;

  // 판매시간 요약 텍스트
  const summary = salesTime?.is_sales_time
    ? `판매시간 ${salesTime.start_time || '--:--'} ~ ${salesTime.end_time || '--:--'}`
    : '판매시간 해제 (상시 판매)';

  return (
    <>
      {step === 'confirm' && (
        <div className="stba-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeAll(); }}>
          <div className="stba-dialog">
            <div className="stba-title">다른 메뉴들도 일괄 적용하시겠습니까?</div>
            <div className="stba-summary">{summary}</div>
            <div className="stba-actions">
              <button className="stba-btn cancel" onClick={closeAll}>아니요</button>
              <button className="stba-btn confirm" onClick={() => setStep('picker')}>네</button>
            </div>
          </div>
        </div>
      )}

      <MenuPickerBottomSheet
        isOpen={step === 'picker'}
        onClose={handlePickerClose}
        storeId={storeId}
        mode="grouped"
        registeredKeys={selectedKeys}
        onSelect={handleSelectMenu}
        onDeselect={handleDeselectMenu}
        showFooter={true}
        onConfirm={handleConfirm}
        title="판매시간 일괄 적용할 메뉴 선택"
      />

      {toast.show && <div className={`stba-toast ${toast.type}`}>{toast.message}</div>}

      <style jsx>{`
        .stba-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          z-index: 10060; display: flex; align-items: center; justify-content: center;
          padding: 24px;
        }
        .stba-dialog {
          background: #fff; border-radius: 16px; padding: 28px;
          width: 100%; max-width: 340px; text-align: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.18);
        }
        .stba-title { font-size: 16px; font-weight: 700; color: #212529; line-height: 1.4; }
        .stba-summary {
          margin-top: 18px; font-size: 14px; font-weight: 600; color: #1971c2;
          background: #e7f5ff; border-radius: 8px; padding: 8px 12px;
        }
        .stba-actions { display: flex; gap: 10px; margin-top: 20px; }
        .stba-btn {
          flex: 1; height: 50px; border-radius: 10px; font-size: 16px; font-weight: 600;
          border: none; cursor: pointer;
        }
        .stba-btn.cancel { background: #f1f3f5; color: #495057; }
        .stba-btn.confirm { background: #FDCD27; color: #FFFFFF; }
        .stba-btn.cancel:active, .stba-btn.confirm:active { transform: scale(0.98); }
        .stba-toast {
          position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
          padding: 10px 18px; border-radius: 22px; font-size: 13px;
          background: #212529; color: #fff; z-index: 10070;
        }
        .stba-toast.error { background: #c92a2a; }
      `}</style>
    </>
  );
};

export default SalesTimeBulkApply;
